import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { driverProfileOf } from '@/lib/driverProfile';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Phone, MapPin, Link, Unlink } from 'lucide-react';
import { useServerPagination } from '@/hooks/useServerPagination';
import { ListPagination } from '@/components/ListPagination';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { useTableSort } from '@/hooks/useTableSort';

const ITEMS_PER_PAGE = 20;

interface DriversRecordsProps {
    onDriverClick: (driverId: number) => void;
}

export default function DriversRecords({ onDriverClick }: DriversRecordsProps) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [contractStatusFilter, setContractStatusFilter] = useState<string>('all');
    const [authFilter, setAuthFilter] = useState<string>('all');

    const { sortKey, sortDirection, handleSort } = useTableSort({ defaultSortKey: 'created_at', defaultSortDirection: 'desc' });

    // ── Step 1: Server-side filters via RPC ──
    const isContractFilterActive = contractStatusFilter !== 'all';
    const isAuthFilterActive = authFilter !== 'all';
    const isFilterActive = isContractFilterActive || isAuthFilterActive;

    const { data: statusFilterIds } = useQuery({
        queryKey: ['driver-status-ids', contractStatusFilter],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_driver_ids_by_contract_status', { p_status: contractStatusFilter });
            if (error) throw error;
            return (data as number[]) || [];
        },
        enabled: isContractFilterActive,
    });

    const { data: authFilterIds } = useQuery({
        queryKey: ['driver-auth-ids', authFilter],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_driver_ids_by_auth_status', { p_status: authFilter });
            if (error) throw error;
            return (data as number[]) || [];
        },
        enabled: isAuthFilterActive,
    });

    // Combine filter ID sets via intersection
    const combinedFilterIds = (() => {
        const sets: number[][] = [];
        if (isContractFilterActive && statusFilterIds !== undefined) sets.push(statusFilterIds);
        if (isAuthFilterActive && authFilterIds !== undefined) sets.push(authFilterIds);
        if (sets.length === 0) return undefined;
        if (sets.length === 1) return sets[0];
        const first = new Set(sets[0]);
        return sets[1].filter(id => first.has(id));
    })();

    // When filter is active but returns no IDs, short-circuit
    const noFilterResults = isFilterActive && combinedFilterIds !== undefined && combinedFilterIds.length === 0;
    const filtersReady = (!isContractFilterActive || statusFilterIds !== undefined) && (!isAuthFilterActive || authFilterIds !== undefined);

    // ── Step 2: Count query ──
    const { data: countData } = useQuery({
        queryKey: ['drivers-count', debouncedSearch, contractStatusFilter, statusFilterIds, authFilter, authFilterIds],
        placeholderData: keepPreviousData,
        queryFn: async () => {
            if (noFilterResults) return 0;

            // Use !inner join when searching so only drivers with matching profiles are counted
            const profileJoin = debouncedSearch
                ? 'profiles!profiles_driver_id_fkey!inner'
                : 'profiles!profiles_driver_id_fkey';

            let query = supabase
                .from('drivers')
                .select(`id, ${profileJoin}(id, admin_id, first_name, last_name, phone)`, { count: 'exact', head: true });

            if (debouncedSearch) {
                query = query.or(
                    `first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%,phone.ilike.%${debouncedSearch}%`,
                    { referencedTable: 'profiles' }
                );
            }
            if (isFilterActive && combinedFilterIds && combinedFilterIds.length > 0) {
                query = query.in('id', combinedFilterIds);
            }

            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
        enabled: filtersReady,
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({
        totalCount: countData || 0,
        itemsPerPage: ITEMS_PER_PAGE,
    });

    useEffect(() => { resetPage(); }, [debouncedSearch, contractStatusFilter, authFilter]);

    // ── Step 3: Data query ──
    const { data: drivers, isLoading } = useQuery({
        queryKey: ['drivers', currentPage, sortKey, sortDirection, debouncedSearch, contractStatusFilter, statusFilterIds, authFilter, authFilterIds],
        placeholderData: keepPreviousData,
        queryFn: async () => {
            if (noFilterResults) return [];

            // Use !inner join when searching so only drivers with matching profiles are returned
            const profileJoin = debouncedSearch
                ? 'profiles:profiles!profiles_driver_id_fkey!inner'
                : 'profiles:profiles!profiles_driver_id_fkey';

            let query = supabase
                .from('drivers')
                .select(`
                    id, created_at, repayment_tag,
                    ${profileJoin}(id, admin_id, first_name, middle_name, last_name, birth_date, phone, photo, address, user_id),
                    contracts(id, created_at, contract_updates(id, status, created_at))
                `)
                .order('created_at', { ascending: sortDirection === 'asc' });

            if (debouncedSearch) {
                query = query.or(
                    `first_name.ilike.%${debouncedSearch}%,last_name.ilike.%${debouncedSearch}%,phone.ilike.%${debouncedSearch}%`,
                    { referencedTable: 'profiles' }
                );
            }
            if (isFilterActive && combinedFilterIds && combinedFilterIds.length > 0) {
                query = query.in('id', combinedFilterIds);
            }

            const { data, error } = await query.range(from, to);
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined && filtersReady,
    });

    // ── Helpers ──
    const getDriverName = (driver: any) => {
        const profile = driverProfileOf(driver.profiles);
        if (!profile) return 'Unknown';
        return `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown';
    };

    const getInitials = (name: string) => name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

    const getLatestContractStatus = (driver: any) => {
        const contracts = driver.contracts || [];
        if (contracts.length === 0) return null;
        const latestContract = contracts.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        const statusUpdates = latestContract?.contract_updates || [];
        if (statusUpdates.length === 0) return null;
        const latestStatus = statusUpdates.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        return latestStatus?.status || null;
    };

    const getContractStatusBadge = (driver: any) => {
        const status = getLatestContractStatus(driver);
        if (!status) return <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-600">No Contract</Badge>;
        switch (status) {
            case 'IN_PROGRESS': return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Active</Badge>;
            case 'COMPLETED': return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Completed</Badge>;
            case 'TERMINATED': return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Terminated</Badge>;
            case 'DRAFT': return <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700">Draft</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getAuthStatusBadge = (driver: any) => {
        const profile = driverProfileOf(driver.profiles);
        const isLinked = profile?.user_id != null;
        return isLinked
            ? <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 gap-1"><Link className="h-3 w-3" />Linked</Badge>
            : <Badge variant="outline" className="border-orange-200 bg-orange-50 text-orange-700 gap-1"><Unlink className="h-3 w-3" />Unlinked</Badge>;
    };

    return (
        <Card className="border-0">
            <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input placeholder="Search drivers..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                    </div>
                    <Select value={contractStatusFilter} onValueChange={setContractStatusFilter}>
                        <SelectTrigger className="w-[180px]"><SelectValue placeholder="Contract Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="IN_PROGRESS">Active</SelectItem>
                            <SelectItem value="COMPLETED">Completed</SelectItem>
                            <SelectItem value="TERMINATED">Terminated</SelectItem>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                            <SelectItem value="NO_CONTRACT">No Contract</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={authFilter} onValueChange={setAuthFilter}>
                        <SelectTrigger className="w-[180px]"><SelectValue placeholder="Auth Account" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Accounts</SelectItem>
                            <SelectItem value="linked">Linked</SelectItem>
                            <SelectItem value="unlinked">Unlinked</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {isLoading ? (
                    <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
                ) : (
                    <>
                        <ListPagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={goToPage} />
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <SortableTableHead sortKey="name" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>Driver</SortableTableHead>
                                        <SortableTableHead>Contact</SortableTableHead>
                                        <SortableTableHead>Address</SortableTableHead>
                                        <SortableTableHead>Contract Status</SortableTableHead>
                                        <SortableTableHead>Auth Account</SortableTableHead>
                                        <SortableTableHead sortKey="created_at" currentSortKey={sortKey} currentSortDirection={sortDirection} onSort={handleSort}>Joined</SortableTableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(drivers || []).map((driver) => {
                                        const profile = driverProfileOf(driver.profiles);
                                        const name = getDriverName(driver);
                                        return (
                                            <TableRow key={driver.id} className="cursor-pointer hover:bg-muted/50" onClick={() => onDriverClick(driver.id)}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <Avatar className="h-9 w-9">
                                                            <SignedAvatarImage src={profile?.photo || undefined} />
                                                            <AvatarFallback className="bg-primary text-primary-foreground text-xs">{getInitials(name)}</AvatarFallback>
                                                        </Avatar>
                                                        <div>
                                                            <p className="font-medium">{name}</p>
                                                            {profile?.birth_date && <p className="text-xs text-muted-foreground">DOB: {new Date(profile.birth_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}</p>}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5 text-sm">
                                                        <Phone className="h-3 w-3 text-muted-foreground" />
                                                        {profile?.phone || '-'}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5 text-sm">
                                                        <MapPin className="h-3 w-3 text-muted-foreground" />
                                                        <span className="max-w-[200px] truncate">{profile?.address || '-'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{getContractStatusBadge(driver)}</TableCell>
                                                <TableCell>{getAuthStatusBadge(driver)}</TableCell>
                                                <TableCell className="text-muted-foreground">{new Date(driver.created_at).toLocaleDateString()}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    {(drivers || []).length === 0 && !isLoading && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                                                {search || isFilterActive ? 'No drivers match your filters' : 'No drivers found'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
